import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT } from "@/lib/data";
import lhgLogo from "@/assets/brand/lhg_mark_color.png";
import lhgLogoWhite from "@/assets/brand/lhg_mark_white.png";

export function LhgWordmark({ dark = false }) {
  return (
    <Link to="/" data-testid="logo-home-link" className="group inline-flex items-center">
      <img
        src={dark ? lhgLogoWhite : lhgLogo}
        alt="Logan Hunter Group, Strategic Business Advisory"
        className="h-24 sm:h-28 w-auto object-contain object-left"
      />
    </Link>
  );
}

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About Scott" },
  { to: "/family", label: "Family of Companies" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-cream/90 backdrop-blur-xl border-b border-blue/15 shadow-[0_1px_40px_rgba(31,78,121,0.10)]"
          : "bg-cream/70 backdrop-blur-md border-b border-white/30"
      }`}
    >
      <div className="container-editorial">
        <div className="flex items-center justify-between h-28 sm:h-32">
          <LhgWordmark />

          <div className="hidden lg:flex items-center gap-5">
            <a
              href={CONTACT.phoneHref}
              data-testid="header-phone-link"
              className="flex items-center gap-2 text-blue hover:text-amber-deep transition-colors"
            >
              <Phone size={15} strokeWidth={2} />
              <span className="font-body text-sm font-bold whitespace-nowrap">{CONTACT.phone}</span>
            </a>
            <Link
              to="/contact"
              data-testid="header-cta-consultation"
              className="group relative overflow-hidden bg-blue text-white px-6 py-3 eyebrow text-[0.6rem]"
            >
              <span className="relative z-10">Book a Consultation</span>
              <span className="absolute inset-0 bg-amber-deep translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-blue p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <nav className="hidden lg:flex items-center justify-center gap-8 border-t border-blue/12 py-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `eyebrow text-[0.66rem] whitespace-nowrap link-underline transition-colors ${
                  isActive ? "text-amber-deep" : "text-blue/85 hover:text-blue"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-cream/97 backdrop-blur-xl border-t border-blue/15"
          >
            <div className="container-editorial py-6 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `font-serif font-bold text-2xl py-2 ${isActive ? "text-amber-deep italic" : "text-blue"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href={CONTACT.phoneHref}
                className="mt-4 flex items-center gap-2 text-blue"
                data-testid="mobile-phone-link"
              >
                <Phone size={16} /> <span className="font-body font-bold">{CONTACT.phone}</span>
              </a>
              <Link
                to="/contact"
                className="mt-4 bg-blue text-white px-6 py-4 text-center eyebrow text-xs"
                data-testid="mobile-cta-consultation"
              >
                Book a Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
